// Webster Notetaker (recall.ai) bot-status vocabulary: palette, labels, and sub-code formatting.
// Kept in a plain module (no components) so the badge/dot stay fast-refresh clean and every surface
// (the meeting editor, the meeting list, the calendar) reads status color from one source of truth.

export type WebsterTone = {
  /** Pill background + text, light and dark. */
  pill: string
  /** Solid dot fill. */
  dot: string
  /** Live state only: the dot pulses (gated on motion-safe at the call site). */
  pulse?: boolean
}

export type WebsterStatusConfig = { label: string; tone: WebsterTone }

// Keyed by the exact API description string (WebsterBotStatus.ToDescription()).
export const WEBSTER_STATUS: Record<string, WebsterStatusConfig> = {
  'Scheduled': {
    label: 'Scheduled',
    tone: { pill: 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-300', dot: 'bg-indigo-500' },
  },
  'In Call': {
    label: 'In call',
    tone: { pill: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300', dot: 'bg-green-500', pulse: true },
  },
  'Processing': {
    label: 'Processing',
    tone: { pill: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300', dot: 'bg-amber-500' },
  },
  'Done': {
    // "Recorded" is a settled, available state — deliberately NOT the live green of "In call" so the
    // two read as different at a glance (the pulse alone isn't enough, and reduced-motion users get none).
    label: 'Recorded',
    tone: { pill: 'bg-sky-100 text-sky-700 dark:bg-sky-900/30 dark:text-sky-300', dot: 'bg-sky-500' },
  },
  'Failed': {
    label: 'Failed',
    tone: { pill: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300', dot: 'bg-red-500' },
  },
  'Unsupported URL': {
    label: 'Unsupported',
    tone: { pill: 'bg-slate-100 text-slate-600 dark:bg-slate-700/60 dark:text-slate-300', dot: 'bg-slate-400' },
  },
  'Not Scheduled': {
    label: 'Not scheduled',
    tone: { pill: 'bg-slate-100 text-slate-600 dark:bg-slate-700/60 dark:text-slate-300', dot: 'bg-slate-300 dark:bg-slate-500' },
  },
}

// "Not Scheduled" means there is no bot; no dot worth showing on a dense list or calendar.
export const WEBSTER_STATUS_SILENT_ON_LIST = new Set(['Not Scheduled'])

/** recall.ai sub-codes arrive as snake_case tokens (e.g. `host_never_joined`). Make them readable. */
export function humanizeSubCode(code: string): string {
  const s = code.replace(/[_-]+/g, ' ').trim()
  return s ? s.charAt(0).toUpperCase() + s.slice(1) : ''
}
